/**
 * Version Watcher — Otomatik yeni build tespiti ve sayfa yenileme.
 *
 * Mantık:
 * 1. Her 60 saniyede `/asset-manifest.json` fetch edilir (cache'siz)
 * 2. main.js hash karşılaştırılır — değişmişse yeni build var
 * 3. Kullanıcıya kısa toast göstererek 5 saniye içinde sayfayı otomatik yeniler
 *
 * Browser cache temizleme:
 * - location.reload() — basit yenileme (2024 sonrası tüm modern browser'larda no-cache HTTP
 *   header'ları sayesinde index.html taze çekilir)
 * - serviceWorker varsa — kayıtlı tüm SW'ları sıfırlar
 * - caches API varsa — tüm cache'leri temizler
 */

const POLL_INTERVAL = 60 * 1000; // 60 saniye
const RELOAD_GRACE_PERIOD = 5 * 1000; // Reload'dan önce 5 sn bekle (kullanıcı toast görsün)

let initialMainHash = null;
let pollTimer = null;
let reloadScheduled = false;

async function fetchManifest() {
  try {
    // Cache-buster query — bu sefer farkına varılması için
    const r = await fetch(`/asset-manifest.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}

function getMainHash(manifest) {
  if (!manifest || !manifest.files) return null;
  const main = manifest.files['main.js'] || '';
  // /static/js/main.45b579e9.js → 45b579e9
  const m = main.match(/main\.([a-f0-9]+)\.js/);
  return m ? m[1] : main;
}

async function clearAllBrowserCaches() {
  // 1. Service Worker varsa kaldır
  if ('serviceWorker' in navigator) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister()));
    } catch {}
  }
  // 2. Cache API'sindeki tüm cache'leri sil
  if ('caches' in window) {
    try {
      const names = await caches.keys();
      await Promise.all(names.map(n => caches.delete(n)));
    } catch {}
  }
}

function showUpdateToast() {
  // Mevcut bir toast varsa duplicate gösterme
  if (document.getElementById('wms-version-update-toast')) return;
  const div = document.createElement('div');
  div.id = 'wms-version-update-toast';
  div.style.cssText = `
    position: fixed; top: 20px; right: 20px; z-index: 99999;
    background: linear-gradient(135deg, #f97316, #ec4899);
    color: white; padding: 14px 18px; border-radius: 10px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.25);
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px; max-width: 360px;
    animation: wmsToastIn 0.3s ease-out;
  `;
  div.innerHTML = `
    <style>@keyframes wmsToastIn { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }</style>
    <div style="font-weight: 600; margin-bottom: 4px;">🔄 Sistem Güncellendi</div>
    <div style="font-size: 12px; opacity: 0.9;">Sayfa otomatik yenilenecek...</div>
  `;
  document.body.appendChild(div);
}

async function checkForUpdate() {
  if (reloadScheduled) return;
  const manifest = await fetchManifest();
  const currentHash = getMainHash(manifest);
  if (!currentHash) return;
  if (initialMainHash == null) {
    initialMainHash = currentHash;
    return;
  }
  if (currentHash !== initialMainHash) {
    // Yeni build tespit edildi
    reloadScheduled = true;
    showUpdateToast();
    if (pollTimer) clearInterval(pollTimer);
    setTimeout(async () => {
      await clearAllBrowserCaches();
      // location.reload(true) — Chrome 86+ için reload-with-cache-bypass
      // Modern browser'larda bu desteklenmediği için query buster ile reload
      const u = new URL(window.location.href);
      u.searchParams.set('_v', currentHash);
      window.location.href = u.toString();
    }, RELOAD_GRACE_PERIOD);
  }
}

export function startVersionWatcher() {
  // Sayfa açılır açılmaz initial hash'i öğren
  checkForUpdate();
  pollTimer = setInterval(checkForUpdate, POLL_INTERVAL);
  // Sayfa visibility değişince (tab tekrar görünür olunca) hemen kontrol et
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkForUpdate();
    }
  });
}

export function stopVersionWatcher() {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
}
